/**
 * The document head is a universal contract: this same component produces the
 * head that is serialized into the SSR response and the head that is mutated
 * during client-side navigation. Head state must therefore stay scoped to the
 * app-owned DocumentHead provider it renders under — see domain/server.md.
 */
import React from 'react'
import { compose } from 'recompose'
import { connect as connectRedux } from 'react-redux'
import type { Map } from 'immutable'
import connectRouter from '../Router/connect'
import {
  DocumentHeadRenderer,
  createDocumentHeadModel,
} from '../DocumentHead'
import type { DocumentHeadLocation } from '../DocumentHead'

export * from '../DocumentHead/constants'

type StateMap = Map<string, unknown>

interface HeadProps {
  interstitial: StateMap
  resolver: StateMap
  page: StateMap
  location: DocumentHeadLocation
  contentLang: string
  isWebView: boolean
  scrollable?: boolean
  jwt?: string | null
}

interface HeadReduxState {
  user: StateMap
  app: StateMap
  interstitial: StateMap
  auth: StateMap
  resolver: StateMap
  page: StateMap
}

function Head ({
  interstitial,
  contentLang,
  scrollable = true,
  isWebView,
  resolver,
  location,
  page,
  jwt = null,
}: HeadProps) {
  const model = createDocumentHeadModel({
    contentLang,
    interstitial,
    isWebView,
    jwt,
    location,
    page,
    resolver,
    scrollable,
  })
  return <DocumentHeadRenderer model={model} />
}

export default compose(
  connectRouter(),
  connectRedux(({
    user, app, interstitial, auth, resolver, page,
  }: HeadReduxState) => ({
    contentLang: user.getIn(['data', 'language', 0], 'en'),
    scrollable: app.get('scrollable', true),
    isWebView: app.get('isWebView', false),
    jwt: auth.get('jwt', null),
    interstitial,
    resolver,
    page,
  })),
)(Head as React.ComponentType<unknown>) as React.ComponentType
